import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/api/client";
import { useTranslation } from "react-i18next";
import { Plus, UserX, UserCheck, KeyRound, Copy, Eye, EyeOff, X, Pencil } from "lucide-react";

// ------- Form scaffolding -----------------------------------------------
const EMPTY = {
  employee_code: "", name: "", email: "", department: "", position: "",
  employment_status: "active", password: "", role: "driver",
  phone_number: "", license_valid_till: "", hotel_mode: false, incentive: 0,
  gender: "unspecified", nationality: "", join_date: "", relieving_date: "",
  remarks: "",
  date_of_birth: "", name_en: "", address: "",
};

const REQUIRED = ["employee_code", "name", "email"];

// Compute age (years) from an ISO date string. Returns "" when input is invalid.
function calcAge(dob) {
  if (!dob) return "";
  const b = new Date(dob);
  if (isNaN(b)) return "";
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age >= 0 && age < 130 ? age : "";
}

const ROLE_LABEL_KEY = {
  driver: "admin.roleDriver",
  report_viewer: "admin.roleReportViewer",
  hr_admin: "admin.roleHrAdmin",
  super_admin: "admin.roleHrAdmin",
};

// Only strings/numbers get sent; date fields must be either ISO string or null.
const cleanForSubmit = (form, includeCode = true) => {
  const out = { ...form };
  if (!includeCode) delete out.employee_code;
  ["license_valid_till", "join_date", "relieving_date", "date_of_birth"].forEach((k) => {
    if (out[k] === "" || out[k] === null) delete out[k];
  });
  if (out.incentive === "" || out.incentive == null) out.incentive = 0;
  else out.incentive = Number(out.incentive);
  if (out.hotel_mode === "true" || out.hotel_mode === true) out.hotel_mode = true;
  else out.hotel_mode = false;
  return out;
};

// ---------------------------------------------------------------------------
// IMPORTANT: These small form widgets MUST be defined at module scope, not
// inside the component. If we re-declare them on every render the input
// element gets a fresh component identity per keystroke → React unmounts +
// remounts the DOM node, focus is lost, and the user can only type one
// character at a time. Moving them out fixes the focus-after-each-keystroke
// bug the user reported.
// ---------------------------------------------------------------------------
function InputField({ label, k, type = "text", state, setState, disabled = false, testid, required = false }) {
  return (
    <div className="field">
      <label className="label">
        {label} {required && <span style={{ color: "#DC2626" }} aria-label="required">*</span>}
      </label>
      <input
        className="input" type={type} disabled={disabled}
        value={state[k] ?? ""}
        onChange={(ev) => setState({ ...state, [k]: ev.target.value })}
        data-testid={testid || `emp-${k}`}
      />
    </div>
  );
}

function GenderField({ state, setState, t }) {
  return (
    <div className="field">
      <label className="label">{t("common.gender")}</label>
      <select className="select" value={state.gender || "unspecified"}
              onChange={(ev) => setState({ ...state, gender: ev.target.value })}
              data-testid="emp-gender">
        <option value="unspecified">{t("common.unspecified")}</option>
        <option value="male">{t("common.male")}</option>
        <option value="female">{t("common.female")}</option>
        <option value="other">{t("common.other")}</option>
      </select>
    </div>
  );
}

function HotelField({ state, setState, t }) {
  return (
    <div className="field">
      <label className="label">{t("common.hotelMode")}</label>
      <div style={{ display: "flex", gap: 12, alignItems: "center", height: 38 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="radio" checked={!!state.hotel_mode} onChange={() => setState({ ...state, hotel_mode: true })}
                 data-testid="emp-hotel-yes" /> {t("common.yes")}
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="radio" checked={!state.hotel_mode} onChange={() => setState({ ...state, hotel_mode: false })}
                 data-testid="emp-hotel-no" /> {t("common.no")}
        </label>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="field">
      <label className="label">{label}</label>
      <div className="input" style={{ background: "#F8FAFC" }}>{value || <span style={{ color: "#94A3B8" }}>—</span>}</div>
    </div>
  );
}

// Password field with an eye-toggle button. Also lives at module scope so the
// same "focus lost per keystroke" trap that hit InputField cannot re-appear.
function PasswordField({ label, state, setState, required }) {
  const [reveal, setReveal] = React.useState(false);
  const inputRef = React.useRef(null);
  const toggle = () => {
    // Preserve caret position across the input-type flip so typed characters
    // continue to append instead of being prepended after Reveal is toggled.
    const el = inputRef.current;
    const pos = el ? el.selectionStart : null;
    setReveal((r) => !r);
    if (el && pos != null) {
      requestAnimationFrame(() => {
        try { el.focus(); el.setSelectionRange(pos, pos); } catch { /* ignore */ }
      });
    }
  };
  return (
    <div className="field">
      <label className="label">
        {label} {required && <span style={{ color: "#DC2626" }} aria-label="required">*</span>}
      </label>
      <div style={{ position: "relative" }}>
        <input
          ref={inputRef}
          className="input"
          type={reveal ? "text" : "password"}
          value={state.password ?? ""}
          onChange={(ev) => setState({ ...state, password: ev.target.value })}
          data-testid="emp-password"
          style={{ paddingRight: 40 }}
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={toggle}
          aria-label={reveal ? "hide password" : "show password"}
          data-testid="emp-password-toggle"
          style={{
            position: "absolute", top: 0, right: 0, height: "100%", width: 40,
            border: 0, background: "transparent", cursor: "pointer", color: "#64748B",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {reveal ? <EyeOff size={16}/> : <Eye size={16}/>}
        </button>
      </div>
    </div>
  );
}

// ------- Component ------------------------------------------------------
export default function EmployeesPage() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState("");
  const [resetInfo, setResetInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [detail, setDetail] = useState(null);       // employee being viewed
  const [editMode, setEditMode] = useState(false);  // toggle in detail modal
  const [editForm, setEditForm] = useState(null);
  const [detailErr, setDetailErr] = useState("");

  const load = () => api.get("/employees").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setErr("");
    // Mandatory-field validation (per user spec: only ID / name / email are required).
    const missing = REQUIRED.filter((k) => !String(form[k] || "").trim());
    if (missing.length) {
      const labels = {
        employee_code: t("admin.empCode"),
        name: t("admin.name"),
        email: t("common.email"),
      };
      setErr(t("common.requiredFields", { fields: missing.map((k) => labels[k]).join(", ") }));
      return;
    }
    if (!form.password || form.password.length < 6) {
      setErr(t("common.requiredFields", { fields: t("common.password") }));
      return;
    }
    try {
      await api.post("/employees", cleanForSubmit(form));
      setForm(EMPTY); setShowForm(false); load();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openDetail = (emp) => { setDetail(emp); setEditMode(false); setEditForm({ ...emp }); setDetailErr(""); };
  const startEdit = () => { setEditForm({ ...detail }); setEditMode(true); setDetailErr(""); };
  const saveEdit = async () => {
    setDetailErr("");
    try {
      // Send only fields the backend accepts on EmployeeUpdate.
      const patch = cleanForSubmit(editForm, false);
      delete patch.password; delete patch.role;
      delete patch.employee_code; delete patch.email;
      const { data } = await api.patch(`/employees/${detail.id}`, patch);
      // Merge server-side extras (user_role, user_is_active) preserved
      setDetail({ ...detail, ...data });
      setEditMode(false); load();
    } catch (e) { setDetailErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const toggleActive = async (emp) => {
    const isActive = emp.employment_status === "active";
    const msg = isActive ? t("admin.confirmDeactivate") : t("admin.confirmActivate");
    if (!window.confirm(msg)) return;
    await api.post(`/employees/${emp.id}/${isActive ? "deactivate" : "activate"}`);
    load();
    if (detail && detail.id === emp.id) setDetail({ ...detail, employment_status: isActive ? "inactive" : "active" });
  };

  const resetPw = async (emp) => {
    if (!window.confirm(t("admin.confirmResetPassword"))) return;
    try {
      const { data } = await api.post(`/employees/${emp.id}/reset-password`);
      setResetInfo(data); setCopied(false);
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const copyPw = async () => {
    try { await navigator.clipboard.writeText(resetInfo.new_password); setCopied(true); } catch { setCopied(false); }
  };

  const roleLabel = (r) => (r ? t(ROLE_LABEL_KEY[r] || r) : "-");
  const roleBadgeClass = (r) => {
    if (r === "driver") return "tag tag-submitted";
    if (r === "report_viewer") return "tag tag-approved";
    if (r === "hr_admin" || r === "super_admin") return "tag tag-returned";
    return "tag tag-draft";
  };

  return (
    <div>
      <h1 className="page-title">{t("admin.employees")}</h1>
      <div style={{ marginBottom: 12 }}>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)} data-testid="emp-add-btn">
          <Plus size={16}/> {t("admin.addEmployee")}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}><div className="card-body">
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 8 }}>{t("common.mandatoryHint")}</div>
          <div className="grid grid-3">
            <InputField label={t("admin.empCode")} k="employee_code" state={form} setState={setForm} required />
            <InputField label={t("admin.name")} k="name" state={form} setState={setForm} required />
            <InputField label={t("common.email")} k="email" type="email" state={form} setState={setForm} required />
            <InputField label={t("common.nameEn")} k="name_en" state={form} setState={setForm} />
            <InputField label={t("common.phoneNumber")} k="phone_number" state={form} setState={setForm} />
            <InputField label={t("common.dateOfBirth")} k="date_of_birth" type="date" state={form} setState={setForm} />
            <InputField label={t("admin.department")} k="department" state={form} setState={setForm} />
            <InputField label={t("admin.position")} k="position" state={form} setState={setForm} />
            <InputField label={t("common.licenseValidTill")} k="license_valid_till" type="date" state={form} setState={setForm} />
            <HotelField state={form} setState={setForm} t={t} />
            <InputField label={t("common.incentive")} k="incentive" type="number" state={form} setState={setForm} />
            <GenderField state={form} setState={setForm} t={t} />
            <InputField label={t("common.nationality")} k="nationality" state={form} setState={setForm} />
            <InputField label={t("common.joiningDate")} k="join_date" type="date" state={form} setState={setForm} />
            <InputField label={t("common.relievingDate")} k="relieving_date" type="date" state={form} setState={setForm} />
            <PasswordField label={t("common.password")} state={form} setState={setForm} required />
            <div className="field">
              <label className="label">{t("admin.role")}</label>
              <select className="select" data-testid="emp-role" value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="driver">{t("admin.roleDriver")}</option>
                <option value="report_viewer">{t("admin.roleReportViewer")}</option>
                <option value="hr_admin">{t("admin.roleHrAdmin")}</option>
              </select>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{t("admin.roleHint")}</div>
            </div>
          </div>
          <div className="field" style={{ marginTop: 8 }}>
            <label className="label">{t("common.address")}</label>
            <textarea className="textarea" rows={2} value={form.address || ""} data-testid="emp-address"
                      onChange={(ev) => setForm({ ...form, address: ev.target.value })} />
          </div>
          <div className="field" style={{ marginTop: 8 }}>
            <label className="label">{t("common.remarks")}</label>
            <textarea className="textarea" rows={3} value={form.remarks || ""} data-testid="emp-remarks"
                      onChange={(ev) => setForm({ ...form, remarks: ev.target.value })} />
          </div>
          {err && <div style={{ color: "#DC2626", marginBottom: 10 }} data-testid="emp-err">{err}</div>}
          <div className="row-actions">
            <button className="btn btn-primary" onClick={save} data-testid="emp-save">{t("common.save")}</button>
            <button className="btn btn-outline" onClick={() => { setForm(EMPTY); setShowForm(false); }}>{t("common.cancel")}</button>
          </div>
        </div></div>
      )}

      {/* ---------- Simplified employee list ---------- */}
      <div className="card"><div className="card-body table-wrap">
        <table className="table" data-testid="emp-table">
          <thead><tr>
            <th>{t("admin.empCode")}</th><th>{t("admin.name")}</th><th>{t("common.email")}</th>
            <th>{t("common.phoneNumber")}</th><th>{t("admin.role")}</th>
            <th>{t("common.hotelMode")}</th><th>{t("common.incentive")}</th>
            <th>{t("common.actions")}</th>
          </tr></thead>
          <tbody>
            {list.map((e) => {
              const active = e.employment_status === "active";
              const role = e.user_role;
              return (
                <tr key={e.id}>
                  <td>{e.employee_code}</td>
                  <td>
                    <div>{e.name}</div>
                    {e.name_en && <div style={{ fontSize: 11, color: "#64748B" }}>{e.name_en}</div>}
                  </td>
                  <td>{e.email}</td>
                  <td>{e.phone_number || "-"}</td>
                  <td><span className={roleBadgeClass(role)} data-testid={`emp-role-${e.id}`}>{roleLabel(role)}</span></td>
                  <td>{e.hotel_mode ? t("common.yes") : t("common.no")}</td>
                  <td>{e.incentive != null ? `¥${Number(e.incentive).toLocaleString()}` : "-"}</td>
                  <td>
                    <div className="row-actions">
                      <button className="btn btn-outline" style={{ padding: "6px 10px", fontSize: 12 }}
                              onClick={() => openDetail(e)} data-testid={`emp-view-${e.id}`}>
                        <Eye size={12}/> {t("common.view")}
                      </button>
                      <button className={active ? "btn btn-danger" : "btn btn-success"}
                              style={{ padding: "6px 10px", fontSize: 12 }}
                              onClick={() => toggleActive(e)} data-testid={`emp-toggle-${e.id}`}>
                        {active ? <UserX size={12}/> : <UserCheck size={12}/>}
                        {active ? t("admin.deactivate") : t("admin.activate")}
                      </button>
                      <button className="btn btn-outline" style={{ padding: "6px 10px", fontSize: 12 }}
                              onClick={() => resetPw(e)} data-testid={`emp-reset-${e.id}`}>
                        <KeyRound size={12}/> {t("admin.resetPassword")}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && <tr><td colSpan="8" style={{ textAlign: "center", color: "#64748B" }}>—</td></tr>}
          </tbody>
        </table>
      </div></div>

      {/* ---------- Detail / Edit modal ---------- */}
      {detail && editForm && (
        <div className="modal-backdrop" data-testid="emp-detail-modal" onClick={() => setDetail(null)}>
          <div className="modal-card" onClick={(ev) => ev.stopPropagation()} style={{ maxWidth: 780, maxHeight: "92vh", overflow: "auto" }}>
            <div className="modal-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span>{detail.employee_code} — {detail.name}</span>
              <div style={{ display: "flex", gap: 6 }}>
                {!editMode ? (
                  <button className="btn btn-primary" style={{ padding: "6px 12px", fontSize: 12 }}
                          onClick={startEdit} data-testid="emp-edit-btn">
                    <Pencil size={12}/> {t("common.edit")}
                  </button>
                ) : (
                  <>
                    <button className="btn btn-primary" style={{ padding: "6px 12px", fontSize: 12 }}
                            onClick={saveEdit} data-testid="emp-edit-save">
                      {t("common.saveChanges")}
                    </button>
                    <button className="btn btn-outline" style={{ padding: "6px 12px", fontSize: 12 }}
                            onClick={() => setEditMode(false)}>{t("common.cancel")}</button>
                  </>
                )}
                <button className="btn btn-outline" style={{ padding: "4px 10px" }} onClick={() => setDetail(null)}>
                  <X size={14}/>
                </button>
              </div>
            </div>

            <div className="grid grid-2" style={{ gap: 10 }}>
              <Row label={t("admin.empCode")} value={detail.employee_code} />
              <Row label={t("common.email")} value={detail.email} />
              {editMode ? <InputField label={t("admin.name")} k="name" state={editForm} setState={setEditForm} testid="emp-e-name" required />
                        : <Row label={t("admin.name")} value={detail.name} />}
              {editMode ? <InputField label={t("common.nameEn")} k="name_en" state={editForm} setState={setEditForm} testid="emp-e-name-en" />
                        : <Row label={t("common.nameEn")} value={detail.name_en} />}
              {editMode ? <InputField label={t("common.phoneNumber")} k="phone_number" state={editForm} setState={setEditForm} testid="emp-e-phone" />
                        : <Row label={t("common.phoneNumber")} value={detail.phone_number} />}
              {editMode ? <InputField label={t("common.dateOfBirth")} k="date_of_birth" type="date" state={editForm} setState={setEditForm} testid="emp-e-dob" />
                        : <Row label={t("common.dateOfBirth")} value={detail.date_of_birth} />}
              <Row label={t("common.age")}
                   value={(() => { const a = calcAge((editMode ? editForm : detail).date_of_birth); return a === "" ? "-" : `${a}`; })()} />
              {editMode ? <InputField label={t("admin.department")} k="department" state={editForm} setState={setEditForm} />
                        : <Row label={t("admin.department")} value={detail.department} />}
              {editMode ? <InputField label={t("admin.position")} k="position" state={editForm} setState={setEditForm} />
                        : <Row label={t("admin.position")} value={detail.position} />}
              {editMode ? <InputField label={t("common.licenseValidTill")} k="license_valid_till" type="date" state={editForm} setState={setEditForm} />
                        : <Row label={t("common.licenseValidTill")} value={detail.license_valid_till} />}
              {editMode ? <HotelField state={editForm} setState={setEditForm} t={t} />
                        : <Row label={t("common.hotelMode")} value={detail.hotel_mode ? t("common.yes") : t("common.no")} />}
              {editMode ? <InputField label={t("common.incentive")} k="incentive" type="number" state={editForm} setState={setEditForm} />
                        : <Row label={t("common.incentive")} value={detail.incentive != null ? `¥${Number(detail.incentive).toLocaleString()}` : "-"} />}
              {editMode ? <GenderField state={editForm} setState={setEditForm} t={t} />
                        : <Row label={t("common.gender")} value={detail.gender ? t(`common.${detail.gender}`) : "-"} />}
              {editMode ? <InputField label={t("common.nationality")} k="nationality" state={editForm} setState={setEditForm} />
                        : <Row label={t("common.nationality")} value={detail.nationality} />}
              {editMode ? <InputField label={t("common.joiningDate")} k="join_date" type="date" state={editForm} setState={setEditForm} />
                        : <Row label={t("common.joiningDate")} value={detail.join_date} />}
              {editMode ? <InputField label={t("common.relievingDate")} k="relieving_date" type="date" state={editForm} setState={setEditForm} />
                        : <Row label={t("common.relievingDate")} value={detail.relieving_date} />}
              <Row label={t("admin.role")} value={<span className={roleBadgeClass(detail.user_role)}>{roleLabel(detail.user_role)}</span>} />
              <Row label={t("admin.employmentStatus")} value={<span className={`tag tag-${detail.employment_status === "active" ? "approved" : "draft"}`}>{detail.employment_status}</span>} />
            </div>

            <div className="field" style={{ marginTop: 10 }}>
              <label className="label">{t("common.address")}</label>
              {editMode ? (
                <textarea className="textarea" rows={2} value={editForm.address || ""} data-testid="emp-e-address"
                          onChange={(ev) => setEditForm({ ...editForm, address: ev.target.value })} />
              ) : (
                <div className="input" style={{ background: "#F8FAFC", minHeight: 44, whiteSpace: "pre-wrap" }} data-testid="emp-detail-address">
                  {detail.address || "-"}
                </div>
              )}
            </div>

            <div className="field" style={{ marginTop: 10 }}>
              <label className="label">{t("common.remarks")}</label>
              {editMode ? (
                <textarea className="textarea" rows={3} value={editForm.remarks || ""} data-testid="emp-e-remarks"
                          onChange={(ev) => setEditForm({ ...editForm, remarks: ev.target.value })} />
              ) : (
                <div className="input" style={{ background: "#F8FAFC", minHeight: 44, whiteSpace: "pre-wrap" }} data-testid="emp-detail-remarks">
                  {detail.remarks || "-"}
                </div>
              )}
            </div>

            {detailErr && <div style={{ color: "#DC2626", marginTop: 8 }} data-testid="emp-edit-err">{detailErr}</div>}
          </div>
        </div>
      )}

      {/* ---------- Reset-password success modal ---------- */}
      {resetInfo && (
        <div className="modal-backdrop" data-testid="reset-pw-modal" onClick={() => setResetInfo(null)}>
          <div className="modal-card" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-title">{t("admin.resetPasswordTitle")}</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 12 }}>{t("admin.resetPasswordHint")}</div>
            <div className="field">
              <label className="label">{t("common.email")}</label>
              <div className="input" style={{ background: "#F1F5F9" }}>{resetInfo.email}</div>
            </div>
            <div className="field">
              <label className="label">{t("common.newPassword")}</label>
              <div className="pw-reveal" data-testid="reset-pw-value">{resetInfo.new_password}</div>
            </div>
            <div className="row-actions">
              <button className="btn btn-outline" onClick={copyPw} data-testid="reset-pw-copy">
                <Copy size={14} /> {copied ? t("common.copied") : t("common.copy")}
              </button>
              <button className="btn btn-primary" onClick={() => setResetInfo(null)}>{t("common.confirm")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
